import { TravelDiaryContentCategory } from './types';

/**
 * Maps category codes (from GET /api/v1/travel-diary-categories) to
 * human-readable English labels for display.
 *
 * The API returns categories with a `code` field (e.g. "practical_info").
 * This map provides the matching display label for each known code.
 */
export const CATEGORY_CODE_LABELS: Record<string, string> = {
  practical_info: 'Practical information',
  general_info: 'General information',
  health_safety: 'Health & safety',
  transport: 'Transportation',
  culture: 'Culture & customs',
  food_drink: 'Food & drink',
  emergency_contacts: 'Emergency contacts',
  travel_assistance: 'Travel assistance',
  insurance_information: 'Insurance information',
  before_you_leave_reminders: 'Before-you-leave reminders',
  baggage_recommendations: 'Baggage recommendations',
  sustainability_commitments: 'Sustainability commitments',
  customer_service_contacts: 'Customer service contacts',
  loyalty_programme_information: 'Loyalty programme information',
  local_etiquette_and_customs: 'Local etiquette and customs',
  local_gastronomy: 'Local gastronomy',
  useful_expressions: 'Useful expressions',
  what_to_pack: 'What to pack',
  practical_checklists: 'Practical checklists',
  weather_and_climate: 'Weather and climate',
  currency_and_tipping: 'Currency and tipping',
  transportation_guidance: 'Transportation guidance',
  shopping_recommendations: 'Shopping recommendations',
  health_and_safety_advice: 'Health and safety advice',
  public_holidays: 'Public holidays',
  hidden_gems: 'Hidden gems',
};

/**
 * Returns a human-readable label for a category code.
 * Falls back to formatting the raw code string if not found in the map.
 *
 * @param code - The category code from the API (e.g. "practical_info")
 */
export const getCategoryLabel = (code: string): string => {
  if (!code) return '';

  // Direct lookup by code
  if (CATEGORY_CODE_LABELS[code]) {
    return CATEGORY_CODE_LABELS[code];
  }

  // Try lowercase version (backward compat with UPPERCASE keys)
  const lowerCode = code.toLowerCase();
  if (CATEGORY_CODE_LABELS[lowerCode]) {
    return CATEGORY_CODE_LABELS[lowerCode];
  }

  // Fallback: format code as readable string (e.g. "practical_info" → "Practical Info")
  return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Builds select options from the API categories array,
 * sorted by displayOrder, using code-to-label matching.
 *
 * @param categories - Array from GET /api/v1/travel-diary-categories
 */
export const buildCategoryOptions = (
  categories: TravelDiaryContentCategory[],
): { value: string; label: string }[] => {
  return [...categories]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((cat) => ({
      value: cat.id,
      label: getCategoryLabel(cat.code),
    }));
};
